// context/AdminChatContext.js - Fixed duplicate message issue

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useChat } from './ChatContext';
import api from '../services/api';
import notification from "../assets/notification_1.mp3";

const AdminChatContext = createContext();

export const AdminChatProvider = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useChat();
  const [activeChats, setActiveChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [onlineAdmins, setOnlineAdmins] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const processedMessageIds = useRef(new Set()); // Track processed message IDs

  // Initialize notification sound
  useEffect(() => {
    notificationSoundRef.current = new Audio(notification);
    notificationSoundRef.current.load();
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      notificationSoundRef.current?.play().catch(() => {});
    } catch (error) {
      console.log('Could not play notification sound');
    }
  };

  // Join all active chat rooms when component mounts
  useEffect(() => {
    if (!socket || !isConnected || user?.role !== 'admin') return;

    console.log('Admin socket connected, joining chat rooms...');

    // Join all active chat rooms
    const joinAllChatRooms = () => {
      activeChats.forEach(chat => {
        socket.emit('joinChat', { chatId: chat.id });
        console.log('Joined chat room:', chat.id);
      });
    };

    joinAllChatRooms();

    // Listen for new messages from users
    const handleNewMessage = (message) => {
      // Check if we've already processed this message
      if (processedMessageIds.current.has(message.id)) {
        console.log('Duplicate message ignored:', message.id);
        return;
      }
      
      // Add to processed set
      processedMessageIds.current.add(message.id);
      
      console.log('New message received from user:', message);
      playNotificationSound();

      // Check if this message is for a chat we're tracking
      const chatExists = activeChats.some(chat => chat.id === message.chat_id);
      
      if (!chatExists) {
        // This is a new chat, refresh the list
        fetchActiveChats();
      }

      // Update messages if this is the selected chat
      if (selectedChat && message.chat_id === selectedChat.id) {
        setMessages(prev => {
          // Check if message already exists in state
          const exists = prev.some(m => m.id === message.id);
          if (exists) return prev;
          return [...prev, message];
        });
      }

      // Update active chats list
      setActiveChats(prev => prev.map(chat => {
        if (chat.id === message.chat_id) {
          const isSelected = selectedChat?.id === chat.id;
          return {
            ...chat,
            last_message_at: new Date(),
            unread_count: isSelected ? 0 : (chat.unread_count || 0) + 1,
            messages: [{ message: message.message, created_at: message.created_at }]
          };
        }
        return chat;
      }));

      // Update unread count
      if (!selectedChat || message.chat_id !== selectedChat.id) {
        setUnreadCount(prev => prev + 1);
      }

      // Clean up processed message IDs after 1 minute to prevent memory leak
      setTimeout(() => {
        processedMessageIds.current.delete(message.id);
      }, 60000);
    };

    // Handle new chat created
    const handleNewChat = (data) => {
      console.log('New chat created:', data);
      
      // Auto-join this new chat room
      socket.emit('joinChat', { chatId: data.chatId });
      
      // Add to active chats
      const newChat = {
        id: data.chatId,
        user_name: data.userName || 'Guest',
        last_message_at: data.timestamp || new Date(),
        unread_count: 1,
        messages: [{ message: data.preview, created_at: data.timestamp }],
        status: 'active'
      };
      
      setActiveChats(prev => {
        // Check if chat already exists
        const exists = prev.some(chat => chat.id === data.chatId);
        if (exists) return prev;
        return [newChat, ...prev];
      });
      
      setUnreadCount(prev => prev + 1);
      playNotificationSound();
    };

    // Handle user typing
    const handleUserTyping = (data) => {
      if (selectedChat && data.chatId === selectedChat.id) {
        setIsTyping(data.isTyping);
        
        if (data.isTyping) {
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
          }, 3000);
        }
      }
    };

    // Handle messages read
    const handleMessagesRead = (data) => {
      if (selectedChat && data.chatId === selectedChat.id && data.readBy === 'user') {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          is_read: true
        })));
      }
    };

    // Handle chat assigned
    const handleChatAssigned = (data) => {
      console.log('Chat assigned:', data);
      
      setActiveChats(prev => prev.map(chat => {
        if (chat.id === data.chatId) {
          return { ...chat, assigned_to: data.adminId };
        }
        return chat;
      }));

      if (selectedChat?.id === data.chatId) {
        setSelectedChat(prev => ({ ...prev, assigned_to: data.adminId }));
      }
    };

    // Handle chat closed
    const handleChatClosed = (data) => {
      console.log('Chat closed:', data);
      
      setActiveChats(prev => prev.filter(chat => chat.id !== data.chatId));
      
      if (selectedChat?.id === data.chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    };

    // Register socket listeners
    socket.on('newMessage', handleNewMessage);
    socket.on('newChat', handleNewChat);
    socket.on('userTyping', handleUserTyping);
    socket.on('messagesRead', handleMessagesRead);
    socket.on('chatAssigned', handleChatAssigned);
    socket.on('chatClosed', handleChatClosed);

    // Cleanup
    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('newChat', handleNewChat);
      socket.off('userTyping', handleUserTyping);
      socket.off('messagesRead', handleMessagesRead);
      socket.off('chatAssigned', handleChatAssigned);
      socket.off('chatClosed', handleChatClosed);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Clear processed message IDs
      processedMessageIds.current.clear();
    };
  }, [socket, isConnected, user, selectedChat, activeChats]);

  // Join specific chat room when selected
  useEffect(() => {
    if (!socket || !isConnected || !selectedChat) return;

    console.log('Joining specific chat room:', selectedChat.id);
    socket.emit('joinChat', { chatId: selectedChat.id });

    return () => {
      // Optionally leave room when unselected
      socket.emit('leaveChat', { chatId: selectedChat.id });
    };
  }, [selectedChat, socket, isConnected]);

  // Fetch active chats
  const fetchActiveChats = useCallback(async () => {
    try {
      const response = await api.get('/chat/admin/chats');
      setActiveChats(response.data);
      
      // Calculate unread count
      const totalUnread = response.data.reduce((sum, chat) => sum + (chat.unread_count || 0), 0);
      setUnreadCount(totalUnread);

      // Join all chat rooms after fetching
      if (socket && isConnected) {
        response.data.forEach(chat => {
          socket.emit('joinChat', { chatId: chat.id });
        });
      }
    } catch (error) {
      console.error('Error fetching active chats:', error);
    }
  }, [socket, isConnected]);

  // Fetch single chat details
  const fetchChatDetails = useCallback(async (chatId) => {
    try {
      setLoading(true);
      const response = await api.get(`/chat/admin/chats/${chatId}`);
      setSelectedChat(response.data);
      setMessages(response.data.messages || []);
      
      // Mark as read when opened
      if (response.data.unread_count > 0) {
        await markChatAsRead(chatId);
        
        // Update unread count in active chats
        setActiveChats(prev => prev.map(chat => 
          chat.id === chatId ? { ...chat, unread_count: 0 } : chat
        ));
        
        // Update total unread count
        setUnreadCount(prev => Math.max(0, prev - response.data.unread_count));
      }

      // Ensure we're in the chat room
      if (socket && isConnected) {
        socket.emit('joinChat', { chatId });
      }
    } catch (error) {
      console.error('Error fetching chat details:', error);
    } finally {
      setLoading(false);
    }
  }, [socket, isConnected]);

  // Mark chat as read
  const markChatAsRead = async (chatId) => {
    try {
      await api.patch(`/chat/admin/chats/${chatId}/read`);
      
      if (socket && isConnected) {
        socket.emit('markAsRead', { chatId });
      }
    } catch (error) {
      console.error('Error marking chat as read:', error);
    }
  };

  // Send message - FIXED: Don't add to local state here, let socket handle it
  const sendMessage = async (chatId, message) => {
    if (!message.trim()) return;

    try {
      // Save via API - this will trigger a socket event
      const response = await api.post('/chat/admin/message', {
        chatId,
        message: message.trim()
      });

      // DON'T add to local state here - socket will handle it
      // The socket event will come back and be processed by handleNewMessage

      // Update last message in active chats (optimistic update)
      setActiveChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { 
              ...chat, 
              last_message_at: new Date(),
              messages: [{ message: message.trim(), created_at: new Date() }]
            }
          : chat
      ));

      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  // Send typing indicator
  const sendTyping = useCallback((chatId, isTyping) => {
    if (socket && isConnected) {
      socket.emit('typing', {
        chatId,
        isTyping
      });
    }
  }, [socket, isConnected]);

  // Assign chat to admin
  const assignChat = async (chatId) => {
    try {
      await api.post(`/chat/admin/chats/${chatId}/assign`);
      
      if (socket && isConnected) {
        socket.emit('assignChat', { chatId });
      }
      
      setActiveChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, assigned_to: user.id } : chat
      ));
      
      if (selectedChat?.id === chatId) {
        setSelectedChat(prev => ({ ...prev, assigned_to: user.id }));
      }
    } catch (error) {
      console.error('Error assigning chat:', error);
    }
  };

  // Close chat
  const closeChat = async (chatId) => {
    try {
      await api.post(`/chat/admin/chats/${chatId}/close`);
      
      if (socket && isConnected) {
        socket.emit('closeChat', { chatId });
      }
      
      setActiveChats(prev => prev.filter(chat => chat.id !== chatId));
      
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error closing chat:', error);
    }
  };

  // Get user info
  const getUserInfo = async (userId, sessionId) => {
    try {
      const response = await api.get('/chat/admin/user-info', {
        params: { userId, sessionId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user info:', error);
      return null;
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Initial fetch
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchActiveChats();
    }
  }, [user, fetchActiveChats]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <AdminChatContext.Provider
      value={{
        activeChats,
        selectedChat,
        messages,
        loading,
        isTyping,
        onlineAdmins,
        notifications,
        unreadCount,
        messagesEndRef,
        fetchActiveChats,
        fetchChatDetails,
        sendMessage,
        sendTyping,
        assignChat,
        closeChat,
        getUserInfo,
        setSelectedChat,
        markChatAsRead,
        isConnected
      }}
    >
      {children}
    </AdminChatContext.Provider>
  );
};

export const useAdminChat = () => useContext(AdminChatContext);